import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  useTheme
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Description as DescriptionIcon,
  Email as EmailIcon,
  Settings as SettingsIcon,
  AccountCircle as AccountCircleIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Assignment as AssignmentIcon,
  Create as CreateIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const Sidebar = ({ onItemClick }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAdmin, isAdminOrManager } = useAuth();

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
      section: 'main'
    },
    {
      text: 'Customers',
      icon: <PeopleIcon />,
      path: '/customers',
      section: 'main'
    },
    {
      text: 'Proposals',
      icon: <DescriptionIcon />,
      path: '/proposals',
      section: 'main'
    }
  ];

  const templateItems = [
    {
      text: 'Proposal Templates',
      icon: <AssignmentIcon />,
      path: '/templates',
      section: 'templates'
    },
    {
      text: 'Email Templates',
      icon: <EmailIcon />,
      path: '/email-templates',
      section: 'templates'
    }
  ];

  const adminItems = [
    {
      text: 'Users',
      icon: <SupervisorAccountIcon />,
      path: '/users',
      section: 'admin',
      adminOnly: true
    },
    {
      text: 'Settings',
      icon: <SettingsIcon />,
      path: '/settings',
      section: 'admin',
      adminOnly: true
    }
  ];

  const userItems = [
    {
      text: 'Profile',
      icon: <AccountCircleIcon />,
      path: '/profile',
      section: 'user'
    }
  ];

  const handleItemClick = (path) => {
    navigate(path);
    if (onItemClick) {
      onItemClick();
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const renderMenuItems = (items, showDivider = false) => {
    const filteredItems = items.filter(item => {
      if (item.adminOnly) return isAdmin();
      if (item.adminOrManagerOnly) return isAdminOrManager();
      return true;
    });

    if (filteredItems.length === 0) return null;

    return (
      <>
        {showDivider && <Divider sx={{ my: 1 }} />}
        <List sx={{ py: 0 }}>
          {filteredItems.map((item) => {
            const active = isActive(item.path);
            return (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  onClick={() => handleItemClick(item.path)}
                  sx={{
                    minHeight: 48,
                    px: 2.5,
                    borderRadius: 1,
                    mx: 1,
                    mb: 0.5,
                    backgroundColor: active ? theme.palette.primary.main : 'transparent',
                    color: active ? theme.palette.primary.contrastText : theme.palette.text.primary,
                    '&:hover': {
                      backgroundColor: active 
                        ? theme.palette.primary.dark 
                        : theme.palette.action.hover,
                    },
                    transition: theme.transitions.create(['background-color', 'color'], {
                      duration: theme.transitions.duration.short,
                    }),
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: 2,
                      color: active ? theme.palette.primary.contrastText : theme.palette.text.secondary,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: active ? 600 : 400,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </>
    );
  };

  const renderSectionHeader = (title) => (
    <Box sx={{ px: 3, py: 1 }}>
      <Typography
        variant="overline"
        sx={{
          fontSize: '0.75rem',
          fontWeight: 600,
          color: theme.palette.text.secondary,
          letterSpacing: '0.1em'
        }}
      >
        {title}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ py: 1 }}>
      {/* Main Navigation */}
      {renderSectionHeader('Main')}
      {renderMenuItems(menuItems)}

      {/* Templates Section */}
      {renderSectionHeader('Templates')}
      {renderMenuItems(templateItems)}

      {/* Admin Section */}
      {isAdmin() && (
        <>
          {renderSectionHeader('Administration')}
          {renderMenuItems(adminItems)}
        </>
      )}

      {/* User Section */}
      {renderSectionHeader('Account')}
      {renderMenuItems(userItems)}

      {/* User Info */}
      <Box
        sx={{
          mt: 2,
          mx: 2,
          p: 2,
          borderRadius: 1,
          backgroundColor: theme.palette.background.default,
          border: `1px solid ${theme.palette.divider}`
        }}
      >
        <Typography variant="body2" fontWeight={600} gutterBottom>
          {user?.firstName} {user?.lastName}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {user?.email}
        </Typography>
        <Typography 
          variant="caption" 
          sx={{
            color: user?.role === 'admin' ? theme.palette.primary.main : theme.palette.text.secondary,
            fontWeight: user?.role === 'admin' ? 600 : 400,
            textTransform: 'capitalize'
          }}
        >
          {user?.role}
        </Typography>
      </Box>
    </Box>
  );
};

export default Sidebar;